import { useState } from "react";
import { addDays, format, parseISO } from "date-fns";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import Heading from "@/components/tpr/Heading/Heading";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useMember } from "@/contexts/memberContext";
import { isMemberAnAdmin } from "@/core/auth";
import { downloadCsv } from "@/lib/csv";
import { useAdminSlotActivity } from "@/hooks/useAdminSlotActivity";

const PAGE_SIZE = 5;

interface Column<T> {
  key: keyof T & string;
  label: string;
}

function SlotSection<T extends object>({
  title,
  rows,
  columns,
  csvName,
  testid,
}: {
  title: string;
  rows: T[];
  columns: Column<T>[];
  csvName: string;
  testid: string;
}) {
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const current = Math.min(page, pageCount - 1);
  const slice = rows.slice(current * PAGE_SIZE, current * PAGE_SIZE + PAGE_SIZE);

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 mb-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold">
          {title} <span className="text-gray-400 font-normal">({rows.length})</span>
        </h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => downloadCsv(`${csvName}.csv`, rows as unknown as Record<string, string | number>[])}
          disabled={rows.length === 0}
          data-testid={`${testid}-export`}
        >
          <Download className="w-3.5 h-3.5" /> Export
        </Button>
      </div>

      {rows.length === 0 ? (
        <p className="text-xs text-gray-500 py-2">No {title.toLowerCase()} this week.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500">
                  {columns.map((c) => (
                    <th key={c.key} className="py-1 pr-3 font-medium">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {slice.map((row, i) => (
                  <tr key={i} className="border-t border-gray-100 dark:border-gray-800">
                    {columns.map((c) => (
                      <td key={c.key} className="py-1 pr-3">
                        {row[c.key] as unknown as string | number}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pageCount > 1 && (
            <div className="flex items-center justify-end gap-2 mt-2 text-xs">
              <button
                type="button"
                className="text-indigo-600 disabled:text-gray-300"
                onClick={() => setPage(current - 1)}
                disabled={current === 0}
              >
                Prev
              </button>
              <span className="text-gray-500">
                {current + 1} / {pageCount}
              </span>
              <button
                type="button"
                className="text-indigo-600 disabled:text-gray-300"
                onClick={() => setPage(current + 1)}
                disabled={current >= pageCount - 1}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const prettyWeek = (weekStart?: string, weekEnd?: string): string => {
  if (!weekStart || !weekEnd) return "";
  // weekEnd is the following Tuesday (exclusive) — show the last day IN the week (weekEnd − 1 day).
  const start = parseISO(weekStart);
  const lastDay = addDays(parseISO(weekEnd), -1);
  return `${format(start, "EEE d MMM")} – ${format(lastDay, "EEE d MMM yyyy")}`;
};

/** Admin-only "Slot activity" block for the dashboard — Created / Changed / Cancelled for a
 *  Tuesday→Tuesday week, with a "back a week" control in case a slot was missed. */
const AdminSlotActivity = () => {
  const { member } = useMember();
  const isAdmin = isMemberAnAdmin(member);
  // weekOffset 0 = this week, −1 = last week, … Pass a date in the target week; the server snaps to
  // the containing Tuesday. (Offset by 7 days from today.)
  const [weekOffset, setWeekOffset] = useState(0);
  const refDate = format(addDays(new Date(), weekOffset * 7), "yyyy-MM-dd");
  const { data, isLoading } = useAdminSlotActivity(isAdmin, refDate);

  if (!isAdmin) return null;

  return (
    <div data-testid="admin-slot-activity" className="mb-8">
      <div className="mb-4 mt-8 flex items-center justify-between gap-3 flex-wrap">
        <Heading title="Slot activity" />
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setWeekOffset((o) => o - 1)}
            data-testid="slot-activity-prev-week"
          >
            <ChevronLeft className="w-4 h-4" /> Previous week
          </Button>
          <span className="text-xs text-gray-600 min-w-[11rem] text-center" data-testid="slot-activity-week-range">
            {prettyWeek(data?.weekStart, data?.weekEnd)}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setWeekOffset((o) => Math.min(0, o + 1))}
            disabled={weekOffset >= 0}
            data-testid="slot-activity-next-week"
          >
            Next week <ChevronRight className="w-4 h-4" />
          </Button>
          {weekOffset !== 0 && (
            <Button variant="ghost" size="sm" onClick={() => setWeekOffset(0)} data-testid="slot-activity-this-week">
              This week
            </Button>
          )}
        </div>
      </div>
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Spinner /> Loading slot activity…
        </div>
      ) : (
        <>
          <SlotSection
            title="Slots Created"
            testid="slots-created"
            csvName="slots-created"
            rows={data?.created ?? []}
            columns={[
              { key: "member", label: "Member" },
              { key: "room", label: "Room" },
              { key: "slotStartDate", label: "Start" },
              { key: "time", label: "Time" },
              { key: "hours", label: "Hrs" },
              { key: "ref", label: "Ref" },
            ]}
          />
          <SlotSection
            title="Slots Changed"
            testid="slots-changed"
            csvName="slots-changed"
            rows={data?.changed ?? []}
            columns={[
              { key: "member", label: "Member" },
              { key: "room", label: "Room" },
              { key: "change", label: "Change" },
              { key: "ref", label: "Ref" },
            ]}
          />
          <SlotSection
            title="Slots Cancelled"
            testid="slots-cancelled"
            csvName="slots-cancelled"
            rows={data?.cancelled ?? []}
            columns={[
              { key: "member", label: "Member" },
              { key: "room", label: "Room" },
              { key: "ref", label: "Ref" },
              { key: "summary", label: "Booking" },
              // "inc" = inclusive: the last date the room was actually used (the slot ran on this day;
              // any cancellations take effect the following week).
              { key: "endDate", label: "Last used (inc)" },
            ]}
          />
        </>
      )}
    </div>
  );
};

export default AdminSlotActivity;
